import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { 
  Edit, Trash2, Plus, Building, User, 
  Filter, Search, Loader2, 
  Eye, Globe, RefreshCw, CheckCircle, CreditCard, FileText, AlertCircle
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import "./PaymentTypeList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const PaymentTypeList = () => {
  const { currentUser, userType, company } = useAuth();
  const navigate = useNavigate();
  
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companies, setCompanies] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);
  
  // For system admins
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');

  useEffect(() => {
    fetchPaymentTypes();
    
    if (isSystemAdmin) {
      fetchCompanies();
    }
  }, [selectedCompany]);

  const fetchPaymentTypes = async () => {
    try {
      setLoading(true);
      
      let endpoint = `${url}/api/paymenttype`;
      const params = {};
      
      if (isSystemAdmin && selectedCompany) {
        params.company_id = selectedCompany;
      }
      
      if (activeOnly) {
        params.status = 'active';
      } else {
        params.status = 'all';
      }
      
      const response = await axios.get(endpoint, { params });
      
      if (response.data.success) {
        setPaymentTypes(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch payment types");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch payment types");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPaymentTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${url}/api/paymenttype/all`);
      
      if (response.data.success) {
        setPaymentTypes(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch all payment types");
      }
    } catch (error) {
      toast.error("Failed to fetch all payment types");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await axios.get(`${url}/api/company`);
      
      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleDelete = async (id, paymentTypeName) => {
    if (!window.confirm(`Are you sure you want to delete payment type "${paymentTypeName}"? This will set it to inactive.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${url}/api/paymenttype/${id}`);
      
      if (response.data.success) {
        toast.success(`Payment type "${paymentTypeName}" deleted successfully`);
        fetchPaymentTypes();
      } else {
        toast.error(response.data.message || "Failed to delete payment type");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete payment type");
    }
  };

  const handleViewPaymentType = (paymentType) => {
    const details = `
Payment Type Details:

Payment Type: ${paymentType.paymentstype}
Payment Type ID: ${paymentType.no || "N/A"}
Description: ${paymentType.description || "Not provided"}
Company: ${paymentType.company_id?.company_name || "N/A"} (${paymentType.company_id?.company_code || "N/A"})
Status: ${paymentType.status === 'active' ? 'Active' : 'Inactive'}
Created By: ${paymentType.created_by?.name || paymentType.created_by?.Usercomp_sure_name || "System"}
Created: ${new Date(paymentType.created_at).toLocaleDateString()}
${paymentType.updated_by ? `\nLast Updated By: ${paymentType.updated_by?.name || paymentType.updated_by?.Usercomp_sure_name || "System"}\nUpdated: ${new Date(paymentType.updated_at).toLocaleDateString()}` : ''}
${paymentType.deleted_at ? `\nDeleted: ${new Date(paymentType.deleted_at).toLocaleDateString()}` : ''}
    `;
    alert(details);
  };

  const handleEditPaymentType = (id) => {
    navigate(`/paymenttype/edit/${id}`);
  };

  // Filter payment types based on search term
  const filteredPaymentTypes = paymentTypes.filter(paymentType =>
    paymentType.paymentstype?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    paymentType.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    paymentType.company_id?.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate stats
  const totalPaymentTypes = paymentTypes.length;
  const activePaymentTypes = paymentTypes.filter(p => p.status === 'active').length;
  const inactivePaymentTypes = paymentTypes.filter(p => p.status === 'inactive').length;
  const paymentTypesWithDescription = paymentTypes.filter(p => p.description && p.description.trim() !== '').length;

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="animate-spin" size={32} />
        <p>Loading payment method...</p>
      </div>
    );
  }

  return (
    <div className="paymenttypes-container">
      {/* Header */}
      <div className="header">
        <div>
          <h2>Payment Method</h2>
          <p className="company-info">
            {isSystemAdmin ? (
              <>
                <Globe size={14} /> All Companies
              </>
            ) : (
              <>
                <Building size={14} /> {company?.company_name} ({company?.company_code})
              </>
            )}
          </p>
        </div>
        
        <div className="header-actions">
          <button 
            className="filter-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filter
          </button>
          
          <button 
            onClick={() => navigate("/AddPaymentType")} 
            className="add-button"
          >
            <Plus size={16} /> Add Payment Method
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-icon">
            <CreditCard size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{totalPaymentTypes}</div>
            <div className="stat-label">Total Payment Method</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon active">
            <CheckCircle size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{activePaymentTypes}</div>
            <div className="stat-label">Active</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon inactive">
            <AlertCircle size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{inactivePaymentTypes}</div>
            <div className="stat-label">Inactive</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon description">
            <FileText size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{paymentTypesWithDescription}</div>
            <div className="stat-label">With Description</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="filters-container">
          <div className="filter-group">
            <label>Filter by Company:</label>
            <div className="select-wrapper">
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                disabled={loadingCompanies || !isSystemAdmin}
              >
                <option value="">All Companies</option>
                {companies.map((comp) => (
                  <option key={comp._id} value={comp._id}>
                    {comp.company_name} ({comp.company_code})
                  </option>
                ))}
              </select>
              <Building size={16} className="select-icon" />
            </div>
          </div>
          
          <div className="filter-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              <span>Show Active Only</span>
            </label>
          </div>
          
          <div className="filter-actions">
            <button 
              className="clear-filters"
              onClick={() => {
                setSelectedCompany("");
                setActiveOnly(true);
              }}
            >
              Clear Filters
            </button>
            
            <button 
              className="apply-filters"
              onClick={() => {
                fetchPaymentTypes();
                setShowFilters(false);
              }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search payment types by name, description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button 
              className="clear-search"
              onClick={() => setSearchTerm("")}
            >
              ✕
            </button>
          )}
        </div>
        
        <div className="search-actions">
          <button 
            className="refresh-btn"
            onClick={isSystemAdmin ? fetchAllPaymentTypes : fetchPaymentTypes}
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
          
          <div className="stats">
            <span>Showing: {filteredPaymentTypes.length} of {totalPaymentTypes} payment Method</span>
            {selectedCompany && (
              <span className="filter-badge">
                <Filter size={12} />
                Filtered
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Payment Types Table */}
      <div className="table-container">
        <table className="styled-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Payment Method</th>
              <th>Description</th>
              {isSystemAdmin && <th>Company</th>}
              <th>Created By</th>
              <th>Created Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPaymentTypes.length === 0 ? (
              <tr>
                <td colSpan={isSystemAdmin ? 8 : 7} className="no-data">
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <CreditCard size={48} color="#ccc" />
                    <p style={{ margin: '15px 0', color: '#666' }}>
                      {searchTerm || selectedCompany
                        ? "No payment types found matching your criteria"
                        : "No payment types found"
                      }
                    </p>
                    <button 
                      className="add-button" 
                      onClick={() => navigate("/AddPaymentType")}
                    >
                      <Plus size={16} /> Create First Payment Method
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              filteredPaymentTypes.map((paymentType, index) => (
                <tr key={paymentType._id}>
                  <td>{paymentType.no || index + 1}</td>
                  
                  <td>
                    <div className="paymenttype-name-cell">
                      <CreditCard size={14} className="mr-2" />
                      <div>
                        <strong>{paymentType.paymentstype}</strong>
                      </div>
                    </div>
                  </td>
                  
                  <td>
                    <div className="description-cell">
                      {paymentType.description ? (
                        <>
                          <FileText size={12} className="mr-2" />
                          <span>{paymentType.description}</span>
                        </>
                      ) : (
                        <span className="no-description">No description</span>
                      )}
                    </div>
                  </td>
                  
                  {isSystemAdmin && (
                    <td>
                      <div className="company-cell">
                        <Building size={12} className="mr-2" />
                        <div>
                          <div>{paymentType.company_id?.company_name || "N/A"}</div>
                          <small>{paymentType.company_id?.company_code || ""}</small>
                        </div>
                      </div>
                    </td>
                  )}
                  
                  <td>
                    <div className="user-cell">
                      <User size={12} className="mr-2" />
                      <div>
                        <div>{paymentType.created_by?.name || paymentType.created_by?.Usercomp_sure_name || "System"}</div>
                        <small>{paymentType.created_by?.email || paymentType.created_by?.Usercomp_email || ""}</small>
                      </div>
                    </div>
                  </td>
                  
                  <td>{new Date(paymentType.created_at).toLocaleDateString()}</td>
                  
                  <td>
                    <span className={`status-badge ${paymentType.status === 'active' ? 'active' : 'inactive'}`}>
                      {paymentType.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn view-btn"
                        onClick={() => handleViewPaymentType(paymentType)}
                        title="View Details"
                      >
                        <Eye size={14} />
                      </button>
                      
                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEditPaymentType(paymentType._id)}
                        title="Edit Payment Type"
                        disabled={paymentType.status === 'inactive'}
                      >
                        <Edit size={14} />
                      </button>
                      
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(paymentType._id, paymentType.paymentstype)}
                        title="Delete Payment Type"
                        disabled={paymentType.status === 'inactive'}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentTypeList;